/**
 * PromptViewerModal — one AI Review model call, as it concerns ONE contact
 * (PRD 2026-10-06, B4: "View prompt: one model call").
 *
 * Opened from a run card on the person panel's AI Review tab. Two reads:
 * - `GET …/prompt` — the system prompt, this contact's part of the user
 *   message (criteria, its own `<candidate>` block, the closing instruction)
 *   and its stored scores in the tool's shape. The API never sends the other
 *   contacts of the batch: only how many there were (`hidden_candidates`).
 * - `GET …/call` — who ran it: model, route, region, tokens, time, cost,
 *   attempts, package/batch.
 *
 * Native `<dialog>` + `showModal()`. Esc (the `cancel` event) or Close calls
 * `onClose`; focus then returns to the card's "View prompt" button, found
 * again by its data attributes (the card may have re-rendered meanwhile).
 */
import type { AiReviewContactPrompt } from "@nu-assist/contracts";
import { X } from "lucide-react";
import { useEffect, useId, useRef } from "react";

import {
  type AiReviewClient,
  type AiReviewContactCall,
  useAiReviewContactCall,
  useAiReviewContactPrompt,
} from "../../clients/aiReview.client";
import {
  fmtCost,
  fmtCount,
  fmtDateTime,
  fmtSeconds,
  modelLabel,
  routeBadge,
} from "../person-panel/format";
import { PromptCallTabs } from "./PromptCallTabs";
import { CopyJsonButton, Kicker, SideRow } from "./viewerParts";

export { MISMATCH_TEXT, RESPONSE_NOTE } from "./PromptCallTabs";

export interface PromptViewerModalProps {
  client: AiReviewClient;
  reviewId: string;
  contactId: string;
  /** "Jump to …" and the fallback title. */
  contactName: string;
  /** The reviewed list, for the title. */
  listTitle: string;
  onClose: () => void;
}

/** The card's "View prompt" button for this contact in this review. */
export function findViewPromptTrigger(reviewId: string, contactId: string): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(
    '[data-testid="ai-review-view-prompt"]'
  )) {
    if (el.dataset.reviewId === reviewId && el.dataset.contactId === contactId) return el;
  }
  return null;
}

/** "package 1, batch 3 · 10 contacts scored in one call", or null when the
 *  call says nothing about its batch. Both indices are 0-based on the wire. */
export function callPlacement(call: AiReviewContactCall | undefined): string | null {
  if (!call || call.status !== "recorded") return null;
  if (call.rescore) return "rescored alone";
  const where =
    call.package_seq != null && call.batch_idx != null
      ? `package ${call.package_seq + 1}, batch ${call.batch_idx + 1}`
      : null;
  const size =
    call.batch_size != null
      ? `${call.batch_size} ${call.batch_size === 1 ? "contact" : "contacts"} scored in one call`
      : null;
  return [where, size].filter(Boolean).join(" · ") || null;
}

/** The JSON a "Copy as JSON" puts on the clipboard: what this view shows and
 *  nothing else — the request carries only this contact's block. */
export function visibleJson(
  reviewId: string,
  contactId: string,
  prompt: AiReviewContactPrompt | undefined,
  call: AiReviewContactCall | undefined
): string {
  return JSON.stringify(
    {
      review_id: reviewId,
      import_contact_id: contactId,
      prompt_status: prompt?.status ?? null,
      prompt_version: prompt?.prompt_version ?? null,
      system_prompt: prompt?.system_prompt ?? null,
      request: prompt?.request ?? null,
      response: prompt?.response ?? null,
      call: call ?? null,
    },
    null,
    2
  );
}

function WhoRanIt({
  call,
  prompt,
}: {
  call: AiReviewContactCall | undefined;
  prompt: AiReviewContactPrompt | undefined;
}) {
  if (!call || call.status !== "recorded") {
    return (
      <p className="text-caption text-text-secondary" data-testid="prompt-call-unavailable">
        The model call behind this score was not recorded.
        {call?.started_by ? ` Run started by ${call.started_by}.` : ""}
      </p>
    );
  }
  const version = call.prompt_version ?? prompt?.prompt_version;
  const promptLine = [
    version ? `AI Review prompt ${version}` : null,
    call.temperature != null ? `temperature ${call.temperature}` : null,
    call.max_tokens != null ? `max ${fmtCount(call.max_tokens)} tokens` : null,
  ].filter(Boolean);
  return (
    <dl className="flex flex-col gap-3" data-testid="prompt-who-ran-it">
      <SideRow label="Model">
        {modelLabel(call.model_id)}
        {call.model_id && (
          <span className="block break-all font-mono text-text-muted">{call.model_id}</span>
        )}
      </SideRow>
      <SideRow label="Route that answered">
        <span className="break-all font-mono">{call.route ?? "—"}</span>
      </SideRow>
      {call.served_region && <SideRow label="Region">{call.served_region}</SideRow>}
      {promptLine.length > 0 && <SideRow label="Prompt">{promptLine.join(" · ")}</SideRow>}
      {call.input_tokens != null && call.output_tokens != null && (
        <SideRow label="Tokens">
          {fmtCount(call.input_tokens)} in · {fmtCount(call.output_tokens)} out
        </SideRow>
      )}
      {call.latency_ms_client != null && (
        <SideRow label="Time">
          {fmtSeconds(call.latency_ms_client)}
          {call.latency_ms_server != null && ` (Bedrock ${fmtSeconds(call.latency_ms_server)})`}
        </SideRow>
      )}
      {call.cost_usd_est != null && (
        <SideRow label="Estimated cost">{fmtCost(call.cost_usd_est)} for the call</SideRow>
      )}
      <SideRow label="Started by">
        {[call.started_by ?? "—", fmtDateTime(call.started_at)].filter(Boolean).join(" · ")}
      </SideRow>
      <SideRow label="Ids">
        <span className="block break-all font-mono" data-testid="prompt-request-id">
          req {call.request_id ?? "—"}
        </span>
        <span className="block break-all font-mono text-text-muted">
          llm_call {call.llm_call_id ?? "—"}
        </span>
      </SideRow>
    </dl>
  );
}

export function PromptViewerModal({
  client,
  reviewId,
  contactId,
  contactName,
  listTitle,
  onClose,
}: PromptViewerModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const promptQuery = useAiReviewContactPrompt({ client, reviewId, contactId });
  const callQuery = useAiReviewContactCall({ client, reviewId, contactId });

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const idsRef = useRef({ reviewId, contactId });
  idsRef.current = { reviewId, contactId };
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = dialogRef.current;
    if (el && !el.open) {
      // happy-dom has no showModal(); the attribute is the same dialog, non-modal.
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", "");
    }
    closeRef.current?.focus();
    // Esc reaches us ONLY as the dialog's `cancel` event; a keydown handler as
    // well would close twice.
    const onCancel = (e: Event) => {
      e.preventDefault();
      onCloseRef.current();
    };
    el?.addEventListener("cancel", onCancel);
    return () => {
      el?.removeEventListener("cancel", onCancel);
      // Close BEFORE the node goes: an open modal dialog removed from the DOM
      // leaves focus on <body>. Then focus the card's button on the next frame.
      if (el?.open && typeof el.close === "function") el.close();
      const refocus = () => {
        const { reviewId: r, contactId: c } = idsRef.current;
        const target = findViewPromptTrigger(r, c) ?? (trigger?.isConnected ? trigger : null);
        target?.focus();
      };
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(refocus);
      else refocus();
    };
  }, []);

  const prompt = promptQuery.data;
  const call = callQuery.data;
  const placement = callPlacement(call);
  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      data-testid="prompt-viewer"
      // The person panel behind this dialog is SUSPENDED while it is open
      // (the host's `onModalChange` → `PersonPanel`'s `suspended`), so its
      // document-level keys are off. Belt and braces for a host that does not
      // wire that: stop Esc here (not preventDefault: the dialog's own
      // `cancel` still closes it) — same as CopyLinkPopover.
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
      className="fixed inset-0 m-auto hidden max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-border bg-surface p-0 text-text shadow-lg backdrop:bg-black/50 open:flex"
    >
      <header className="flex flex-col gap-3 border-b border-border px-6 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <Kicker>AI Review · Model call</Kicker>
          <h2 id={titleId} className="text-h3 font-semibold text-text" data-testid="prompt-title">
            {listTitle}
            {placement ? ` — ${placement}` : ""}
          </h2>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <CopyJsonButton text={visibleJson(reviewId, contactId, prompt, call)} />
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="prompt-close"
            className="rounded-md p-1 text-text-muted hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-4 px-6 py-4">
          <PromptCallTabs
            client={client}
            reviewId={reviewId}
            contactId={contactId}
            contactName={contactName}
          />
        </div>
        <aside
          aria-label="Who ran it"
          className="border-t border-border px-6 py-4 lg:w-72 lg:shrink-0 lg:border-l lg:border-t-0"
        >
          <h3 className="mb-3 text-label font-semibold text-text">Who ran it</h3>
          {callQuery.isPending ? (
            <p className="text-caption text-text-muted">Loading…</p>
          ) : callQuery.isError ? (
            <p className="text-caption text-text-secondary" data-testid="prompt-call-error">
              The call details could not be loaded.
            </p>
          ) : (
            <WhoRanIt call={call} prompt={prompt} />
          )}
          {call?.status === "recorded" && routeBadge(call.route) === "global." && (
            <p className="mt-3 text-caption text-text-muted">
              Answered on the global. backup route.
            </p>
          )}
        </aside>
      </div>
    </dialog>
  );
}
